/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import Dialog, { DialogProps } from "@mui/material/Dialog";
import React, { ReactNode } from "react";
import DialogTitle, { DialogTitleProps } from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import { Divider, SxProps, Theme } from "@mui/material";
import IconButtonLegalbird from "@/src/components/legalbird/IconButtonLegalbird/IconButtonLegalbird.tsx";
import dialogModalStyle from "@/src/components/DialogModal/dialogModalStyle.ts";

const VARIANT_CONFIG = {
  divider: {
    titleVariant: "h7",
    titleAlign: "left",
    showDivider: true,
    paperSx: {
      padding: "2rem",
    },
  },
  clean: {
    titleVariant: "h6",
    titleAlign: "center",
    showDivider: false,
    paperSx: {
      padding: {
        xs: "3rem 1rem",
        sm: "3rem 4rem",
      },
    },
  },
} satisfies Record<
  string,
  {
    titleVariant?: DialogTitleProps["variant"];
    titleAlign: "left" | "center";
    showDivider: boolean;
    paperSx: SxProps<Theme>;
  }
>;

export type DialogModalVariant = keyof typeof VARIANT_CONFIG;

type DialogModalProps = {
  open: boolean;
  onClose?: () => void;
  actions?: ReactNode;
  responsive?: boolean;
  title: string;
  titleVariant?: DialogTitleProps["variant"];
  variant?: DialogModalVariant;
  children: ReactNode;
} & DialogProps;

const DialogModal = ({
  open,
  onClose,
  title,
  titleVariant,
  children,
  actions,
  responsive = false,
  variant = "divider",
  maxWidth = "md",
  fullWidth = true,
  ...rest
}: DialogModalProps) => {
  const fullScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const config = VARIANT_CONFIG[variant];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={responsive && fullScreen}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      slotProps={{
        paper: {
          sx: config.paperSx,
        },
      }}
      {...rest}
    >
      {!!onClose && (
        <IconButtonLegalbird label="Dialog schließen" variant="text" color="primary" onClick={onClose} sx={dialogModalStyle.closeButton}>
          <CloseIcon />
        </IconButtonLegalbird>
      )}

      <DialogTitle
        variant={titleVariant ?? config.titleVariant}
        sx={(theme) => ({
          color: theme.palette.text.primary,
          textAlign: config.titleAlign,
          paddingBottom: "0rem",
        })}
      >
        {title}
      </DialogTitle>

      {config.showDivider && <Divider sx={dialogModalStyle.divider} />}

      <DialogContent sx={{marginTop: "1.5rem"}}>{children}</DialogContent>

      {!!actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  );
};

export default DialogModal;
